
import { defineComponent, PropType } from "vue";
import { AxiosResponse } from "axios";
import { formatDate, luxonFormatDate } from "@/helpers/commons/localeHelper";
import { iDocument, iParameter, iAdmissibleValues } from "./KnParameterSidebar";
import { setVisualDependency, updateVisualDependency } from "./KnParameterSidebarVisualDependency";
import { setDataDependency, updateDataDependency } from "./KnParameterSidebarDataDependency";
import { setLovsDependency, updateLovDependency } from "./KnParameterSidebarLovsDependency";
import Calendar from "primevue/calendar";
import Chip from "primevue/chip";
import Chips from "primevue/chips";
import Checkbox from "primevue/checkbox";
import Dropdown from "primevue/dropdown";
import KnParameterPopupDialog from "./dialogs/KnParameterPopupDialog.vue";
import KnParameterTreeDialog from "./dialogs/KnParameterTreeDialog.vue";
import KnParameterSaveDialog from "./dialogs/KnParameterSaveDialog.vue";
import KnParameterSavedParametersDialog from "./dialogs/KnParameterSavedParametersDialog.vue";
import Menu from "primevue/menu";
import MultiSelect from "primevue/multiselect";
import RadioButton from "primevue/radiobutton";
import ScrollPanel from "primevue/scrollpanel";
import { getCorrectRolesForExecutionForType } from "../../../helpers/commons/roleHelper";

export default defineComponent({
  name: "kn-parameter-sidebar",
  components: {
    Calendar,
    Chip,
    Chips,
    Checkbox,
    Dropdown,
    KnParameterPopupDialog,
    KnParameterTreeDialog,
    KnParameterSaveDialog,
    KnParameterSavedParametersDialog,
    Menu,
    MultiSelect,
    RadioButton,
    ScrollPanel,
  },
  props: {
    filtersData: { type: Object },
    propDocument: { type: Object },
    userRole: { type: Object as PropType<String | null> },
    propMode: { type: String },
    propQBEParameters: { type: Array },
    dateFormat: { type: String },
    dataset: { type: Object },
    correctRolesForExecution: { type: Array },
    loadFromDatasetManagement: { type: Boolean, default: false },
  },
  emits: ["execute", "exportCSV", "roleChanged", "parametersChanged"],
  data() {
    return {
      document: null as iDocument | null,
      parameters: { isReadyForExecution: false, filterStatus: [] } as {
        filterStatus: iParameter[];
        isReadyForExecution: boolean;
      },
      executeMenuItems: [] as { label: string; command: Function }[],
      selectedParameterCheckbox: {} as any,
      popupDialogVisible: false,
      selectedParameter: null as iParameter | null,
      parameterPopUpData: null as iAdmissibleValues | null,
      treeDialogVisible: false,
      formatedParameterValues: null as any,
      parameterSaveDialogVisible: false,
      savedParametersDialogVisible: false,
      viewpoints: [],
      user: null as any,
      role: null as string | null,
      loading: false,
      updateVisualDependency,
      mode: "execution",
      qbeParameters: [] as any,
      primary: true,
      userDateFormat: "" as string,
      availableRolesForExecution: [] as any,
    };
  },
  watch: {
    sessionRole() {
      this.role = "";
      this.parameters = { isReadyForExecution: false, filterStatus: [] };
    },
    filtersData() {
      this.loadDocument();
      this.loadParameters();
    },
    userRole() {
      this.role = this.userRole as string;
    },
    propMode() {
      this.loadMode();
    },
    propQBEParameters() {
      this.loadQBEParameters();
    },
    dateFormat() {
      this.userDateFormat = this.dateFormat as string;
    },
  },
  computed: {
    sessionRole(): string {
      return (this.$store.state as any).user.sessionRole;
    },
    buttonsDisabled(): boolean {
      return this.loading || this.requiredFiledMissing();
    },
    positionClass(): string {
      return this.document?.parametersRegion ? "kn-parameter-sidebar-" + this.document.parametersRegion : "kn-parameter-sidebar";
    },
  },
  mounted() {
    this.loadMode();
    if (this.mode === "qbeView" || this.mode === "workspaceView" || this.mode === "datasetManagement") this.loadQBEParameters();

    this.user = (this.$store.state as any).user;
    this.role = this.userRole as string;
    this.loadDocument();
    this.loadParameters();
    this.userDateFormat = this.dateFormat as string;
  },
  methods: {
    applyFieldClass(cssClass: string): string {
      let cssCompleteClass = this.primary ? cssClass + " fieldBackgroundColorPrimary" : cssClass + " fieldBackgroundColorSecondary";
      this.primary = !this.primary;
      return cssCompleteClass;
    },
    setNewSessionRole() {
      this.$emit("roleChanged", this.role);
      this.parameters = { isReadyForExecution: false, filterStatus: [] };
    },
    async loadDocument() {
      this.document = this.propDocument as iDocument;

      if (this.correctRolesForExecution) {
        this.availableRolesForExecution = this.correctRolesForExecution;
      } else {
        let typeCode = "DOCUMENT";
        let id = this.document.id;
        if (this.document.type === "businessModel") {
          typeCode = "DATAMART";
        } else if (this.document.dsTypeCd) {
          typeCode = "DATASET";
        } else if (this.document.type == "federatedDataset" && this.document.federation_id) {
          typeCode = "FEDERATED_DATASET";
          id = this.document.federation_id;
        }

        getCorrectRolesForExecutionForType(typeCode, id, this.document.label).then((response: any) => {
          this.availableRolesForExecution = response;
          if (!this.role && this.availableRolesForExecution.length == 1) {
            this.role = this.availableRolesForExecution[0];
            this.setNewSessionRole();
          }
        });
      }
    },
    loadParameters() {
      this.parameters.isReadyForExecution = this.filtersData?.isReadyForExecution;
      this.parameters.filterStatus = [];
      this.filtersData?.filterStatus?.forEach((el: iParameter) => {
        if (el.selectionType == "LIST" && el.showOnPanel == "true" && el.visible && el.multivalue) {
          this.selectedParameterCheckbox[el.id] = el.parameterValue?.map((parameterValue: any) => parameterValue.value);
        } else if (el.type === "STRING" && el.valueSelection == "man_in" && el.showOnPanel == "true") {
          el.initialValue = el.parameterValue && el.parameterValue[0] ? el.parameterValue[0].value : "";
        }
        this.parameters.filterStatus.push(el);
      });
      this.parameters?.filterStatus.forEach((el: any) => setVisualDependency(this.parameters, el));
      this.parameters?.filterStatus.forEach((el: any) => setDataDependency(this.parameters, el));
      this.parameters?.filterStatus.forEach((el: any) => setLovsDependency(this.parameters, el));
      this.parameters?.filterStatus.forEach((el: any) => this.updateVisualDependency(el));
    },
    setDataDependency(parameter: iParameter) {
      if (parameter.dependencies.data.length !== 0) {
        parameter.dependencies.data.forEach((dependency: any) => {
          const index = this.parameters.filterStatus.findIndex((param: any) => {
            return param.urlName === dependency.parFatherUrlName;
          });
          if (index !== -1) {
            const tempParameter = this.parameters.filterStatus[index];
            parameter.dataDependsOnParameters ? parameter.dataDependsOnParameters.push(tempParameter) : (parameter.dataDependsOnParameters = [tempParameter]);
            tempParameter.dataDependentParameters ? tempParameter.dataDependentParameters.push(parameter) : (tempParameter.dataDependentParameters = [parameter]);
          }
        });
      }
    },
    resetParameterValue(parameter: any) {
      if (!parameter.driverDefaultValue) {
        if (parameter.multivalue) {
          parameter.parameterValue = [];
          this.selectedParameterCheckbox[parameter.id] = [];
        } else {
          parameter.parameterValue[0] = { value: "", description: "" };
        }
        this.updateParameterDependencyAfterParameterResetDefaultValue(parameter);
        return;
      }

      const valueColumn = parameter.metadata.valueColumn;
      const descriptionColumn = parameter.metadata.descriptionColumn;
      let valueIndex = null as any;
      if (parameter.metadata.colsMap) {
        valueIndex = Object.keys(parameter.metadata.colsMap).find((key: string) => parameter.metadata.colsMap[key] === valueColumn);
      }
      let descriptionIndex = null as any;
      if (parameter.metadata.colsMap) {
        descriptionIndex = Object.keys(parameter.metadata.colsMap).find((key: string) => parameter.metadata.colsMap[key] === descriptionColumn);
      }
      if ((parameter.selectionType === "LIST" || parameter.selectionType === "COMBOBOX") && parameter.showOnPanel === "true" && parameter.visible && parameter.multivalue) {
        parameter.parameterValue = [] as {
          value: string;
          description: string;
        }[];
        this.selectedParameterCheckbox[parameter.id] = [];
        for (let i = 0; i < parameter.driverDefaultValue.length; i++) {
          const temp = parameter.driverDefaultValue[i];
          parameter.parameterValue.push({
            value: valueIndex ? temp[valueIndex] : "",
            description: descriptionIndex ? temp[descriptionIndex] : "",
          });
          if (valueIndex) {
            this.selectedParameterCheckbox[parameter.id].push(temp[valueIndex]);
          }
        }
      } else if (parameter.selectionType === "TREE" && parameter.showOnPanel === "true" && parameter.visible && parameter.multivalue) {
        parameter.parameterValue = parameter.driverDefaultValue.map((el: any) => {
          return { value: valueIndex ? el[valueIndex] : "", description: descriptionIndex ? el[descriptionIndex] : "" };
        });
      } else if (parameter.selectionType === "TREE" && parameter.showOnPanel === "true" && parameter.visible && !parameter.multivalue) {
        parameter.parameterValue[0] = {
          value: parameter.driverDefaultValue[0][valueIndex],
          description: parameter.driverDefaultValue[0][descriptionIndex],
        };
      } else if ((parameter.selectionType === "COMBOBOX" || parameter.selectionType === "LOOKUP") && parameter.showOnPanel === "true" && parameter.visible && !parameter.multivalue) {
        parameter.parameterValue[0] = {
          value: parameter.driverDefaultValue[0][valueIndex],
          description: parameter.driverDefaultValue[0][descriptionIndex],
        };
      } else if (parameter.selectionType === "LOOKUP" && parameter.showOnPanel === "true" && parameter.visible && parameter.multivalue) {
        parameter.parameterValue = parameter.driverDefaultValue.map((el: any) => {
          return {
            value: valueIndex ? el[valueIndex] : "",
            description: descriptionIndex ? el[descriptionIndex] : "",
          };
        });
      } else if (parameter.type === "DATE" && parameter.showOnPanel === "true" && parameter.visible) {
        if (parameter.driverDefaultValue[0].desc?.split("#")[0]) {
          parameter.parameterValue[0].value = luxonFormatDate(parameter.driverDefaultValue[0].desc?.split("#")[0], parameter.driverDefaultValue[0].desc?.split("#")[1]);
        }
      } else {
        if (!parameter.parameterValue[0]) {
          parameter.parameterValue[0] = { value: "", description: "" };
        }
        parameter.parameterValue[0].value = parameter.driverDefaultValue[0].value ?? parameter.driverDefaultValue[0][valueIndex];
      }
      this.updateParameterDependencyAfterParameterResetDefaultValue(parameter);
    },
    updateParameterDependencyAfterParameterResetDefaultValue(parameter: any) {
      parameter.dependentParameters?.forEach((el: any) => this.updateDependency(el, true));
      parameter.dataDependentParameters?.forEach((el: any) => this.updateDependency(el, true));
      parameter.lovDependentParameters?.forEach((el: any) => this.updateDependency(el, true));
    },
    resetAllParameters() {
      this.parameters.filterStatus.forEach((el: any) => this.resetParameterValue(el));
      this.parameters.filterStatus.forEach((el: any) => this.updateDependency(el));
    },
    toggle(event: Event) {
      this.createMenuItems();
      const menu = this.$refs.executeButtonMenu as any;
      menu.toggle(event);
    },
    createMenuItems() {
      this.executeMenuItems = [];
      this.executeMenuItems.push({
        label: this.$t("common.exportCSV"),
        command: () => this.$emit("exportCSV"),
      });
    },
    requiredFiledMissing() {
      if (this.mode === "qbeView" || this.mode === "workspaceView") {
        for (let i = 0; i < this.qbeParameters.length; i++) {
          if (!this.qbeParameters[i].value) {
            return true;
          }
        }
      }

      for (let i = 0; i < this.parameters.filterStatus.length; i++) {
        const parameter = this.parameters.filterStatus[i];
        if (parameter.mandatory) {
          if (!parameter.parameterValue || parameter.parameterValue.length === 0) {
            return true;
          } else {
            for (let i = 0; i < parameter.parameterValue.length; i++) {
              if (!parameter.parameterValue[i].value) {
                return true;
              }
            }
          }
        }
      }
      return false;
    },
    setRadioButtonValue(parameter: iParameter) {
      const index = parameter.data?.findIndex((el: any) => el.value === parameter.parameterValue[0].value);
      if (index !== -1) parameter.parameterValue[0].description = parameter.data[index].description;
      this.updateDependency(parameter);
    },
    setCheckboxValue(parameter: iParameter) {
      parameter.parameterValue = this.selectedParameterCheckbox[parameter.id].map((el: any) => {
        return { value: el, description: el };
      });
      this.updateDependency(parameter);
    },
    openPopupDialog(parameter: iParameter) {
      this.selectedParameter = parameter;
      this.getParameterPopupInfo(parameter);
      this.popupDialogVisible = true;
    },
    openTreeDialog(parameter: iParameter) {
      this.selectedParameter = parameter;
      this.formatedParameterValues = this.getFormattedParameters();
      this.treeDialogVisible = true;
    },
    onTreeClose() {
      this.selectedParameter = null;
      this.formatedParameterValues = null;
      this.treeDialogVisible = false;
    },
    async getParameterPopupInfo(parameter: iParameter) {
      this.loading = true;
      const role = this.sessionRole && this.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.sessionRole : this.role;
      const postData = {
        label: this.document?.label,
        parameters: this.getFormattedParameters(),
        paramId: parameter.urlName,
        role: role,
      };

      let url = "2.0/documentExeParameters/admissibleValues";
      if (this.mode !== "execution" && this.document) {
        url = this.document.type === "businessModel" ? `1.0/businessmodel/${this.document.name}/admissibleValues` : `3.0/datasets/${this.document.label}/admissibleValues`;
      }

      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + url, postData)
        .then((response: AxiosResponse<any>) => (this.parameterPopUpData = response.data))
        .catch((error: any) => console.log("ERROR: ", error));
      this.loading = false;
    },
    getFormattedParameters() {
      let parameters = [] as any[];
      Object.keys(this.parameters.filterStatus).forEach((key: any) => {
        const parameter = this.parameters.filterStatus[key];
        if (!parameter.multivalue && parameter.parameterValue[0]) {
          parameters.push({
            urlName: parameter.urlName,
            value: parameter.parameterValue[0].value ?? "",
            description: parameter.parameterValue[0].description ?? "",
          });
        } else {
          parameters.push({
            urlName: parameter.urlName,
            value: parameter.parameterValue?.map((el: any) => el.value),
            description: parameter.parameterDescription ?? "",
          });
        }
      });
      return parameters;
    },
    getParameterValues() {
      let parameters = {} as any;
      Object.keys(this.parameters.filterStatus).forEach((key: any) => {
        const parameter = this.parameters.filterStatus[key];
        if (parameter.type === "DATE") {
          parameters[parameter.urlName] = parameter.parameterValue[0].value;
          parameters[parameter.urlName + "_field_visible_description"] = parameter.parameterValue[0].value;
        } else if (parameter.valueSelection === "man_in" && !parameter.multivalue) {
          parameters[parameter.urlName] = parameter.type === "NUM" ? +parameter.parameterValue[0].value : parameter.parameterValue[0].value;
          parameters[parameter.urlName + "_field_visible_description"] = parameter.type === "NUM" ? +parameter.parameterValue[0].description : parameter.parameterValue[0].description;
        } else if (parameter.selectionType === "TREE" || parameter.selectionType === "LOOKUP" || parameter.multivalue) {
          parameters[parameter.urlName] = parameter.parameterValue.map((el: any) => el.value);
          let tempString = "";
          for (let i = 0; i < parameter.parameterValue.length; i++) {
            tempString += parameter.parameterValue[i].description;
            tempString += i === parameter.parameterValue.length - 1 ? "" : ";";
          }
          parameters[parameter.urlName + "_field_visible_description"] = tempString;
        } else {
          parameters[parameter.urlName] = parameter.parameterValue[0].value;
          parameters[parameter.urlName + "_field_visible_description"] = parameter.parameterValue[0].description;
        }
      });
      return parameters;
    },
    onPopupSave(parameter: iParameter) {
      this.updateDependency(parameter);
      this.popupDialogVisible = false;
    },
    onTreeSave(parameter: iParameter) {
      this.updateVisualDependency(parameter);
      this.treeDialogVisible = false;
    },
    async updateDependency(parameter: iParameter, resetValue: boolean = false) {
      this.loading = true;
      const role = this.sessionRole && this.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.sessionRole : this.role;
      this.updateVisualDependency(parameter);
      await updateDataDependency(this.parameters, parameter, this.loading, this.document, role, this.$http, this.mode, resetValue, this.userDateFormat);
      await updateLovDependency(this.parameters, parameter, this.loading, this.document, role, this.$http, this.mode, resetValue, this.userDateFormat);
      this.$emit("parametersChanged", {
        parameters: this.parameters,
        document: this.propDocument,
      });
      this.loading = false;
    },
    openSaveParameterDialog() {
      this.parameterSaveDialogVisible = true;
    },
    async saveViewpoint(viewpoint: any) {
      const role = this.sessionRole && this.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.sessionRole : this.role;

      if (!role) return;

      const postData = {
        ...viewpoint,
        OBJECT_LABEL: this.document?.label,
        ROLE: role,
        VIEWPOINT: this.getParameterValues(),
      };
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentviewpoint/addViewpoint`, postData)
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.createTitle"),
            msg: this.$t("common.toast.success"),
          });
          this.parameterSaveDialogVisible = false;
        })
        .catch(() => {});
      this.loading = false;
    },
    async openSavedParametersDialog() {
      const role = this.sessionRole && this.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.sessionRole : this.role;
      if (!role) return;
      this.loading = true;
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentviewpoint/getViewpoints?label=${this.document?.label}&role=${role}`).then((response: AxiosResponse<any>) => {
        this.viewpoints = response.data.viewpoints;
        this.savedParametersDialogVisible = true;
      });
      this.loading = false;
    },
    fillParameterForm(viewpoint: any) {
      const tempParameters = this.decodeViewpointPrameterValues(viewpoint.vpValueParams);

      Object.keys(tempParameters)?.forEach((key: any) => {
        const index = this.parameters.filterStatus.findIndex((el: any) => el.urlName === key);
        if (index !== -1) {
          const parameter = this.parameters.filterStatus[index];
          if (parameter.type === "DATE") {
            const temp = new Date(tempParameters[key]);
            parameter.parameterValue[0].value = temp instanceof Date && !isNaN(temp as any) ? temp : this.getFormattedDate(tempParameters[key], "DD/MM/YYYY");
            parameter.parameterValue[0].value = temp as any;
          } else if ((parameter.valueSelection === "man_in" || parameter.selectionType === "COMBOBOX") && !parameter.multivalue) {
            parameter.parameterValue[0].value = tempParameters[key];
            parameter.parameterValue[0].description = tempParameters[key + "_field_visible_description"];
          } else if (parameter.selectionType === "TREE" || parameter.selectionType === "LOOKUP" || parameter.multivalue) {
            const tempArrayValues = JSON.parse(tempParameters[key]);
            const tempArrayDescriptions = tempParameters[key + "_field_visible_description"].split(";");
            parameter.parameterValue = [];
            for (let i = 0; i < tempArrayValues.length; i++) {
              parameter.parameterValue[i] = {
                value: tempArrayValues[i],
                description: tempArrayDescriptions[i] ?? "",
              };
            }
            if (parameter.selectionType === "LIST") {
              this.selectedParameterCheckbox[parameter.id] = parameter.parameterValue?.map((parameterValue: any) => parameterValue.value);
            }
          } else {
            parameter.parameterValue[0].value = tempParameters[key];
            parameter.parameterValue[0].description = tempParameters[key + "_field_visible_description"];
          }
        }
        this.savedParametersDialogVisible = false;
      });
    },
    getFormattedDate(date: any, format: any) {
      return formatDate(date, "l", format);
    },
    decodeViewpointPrameterValues(string: string) {
      const parametersJson = {};
      const parameterArray = string.split("%26");
      for (let i = 0; i < parameterArray.length; i++) {
        const temp = parameterArray[i].split("%3D");
        parametersJson[temp[0]] = temp[1];
      }
      return parametersJson;
    },
    executeViewpoint(viewpoint: any) {
      this.fillParameterForm(viewpoint);
      this.$emit("execute");
      this.savedParametersDialogVisible = false;
    },
    async deleteViewpoint(viewpoint: any) {
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentviewpoint/deleteViewpoint`, { VIEWPOINT: "" + viewpoint.vpId })
        .then(async () => {
          this.removeViewpoint(viewpoint);
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.deleteTitle"),
            msg: this.$t("common.toast.deleteSuccess"),
          });
        })
        .catch(() => {});
      this.loading = false;
    },
    removeViewpoint(viewpoint: any) {
      const index = this.viewpoints.findIndex((el: any) => el.vpId === viewpoint.vpId);
      if (index !== -1) this.viewpoints.splice(index, 1);
    },
    loadMode() {
      this.mode = this.propMode ? this.propMode : "execution";
    },
    loadQBEParameters() {
      this.qbeParameters = [];
      this.propQBEParameters?.forEach((parameter: any) => {
        if (parameter.multiValue && parameter.defaultValue && !Array.isArray(parameter.defaultValue)) {
          parameter.defaultValue = parameter.defaultValue?.split(",").map((el: any) => el.trim());
        }
        if (!parameter.value) parameter.value = parameter.defaultValue;
        this.qbeParameters.push(parameter);
      });
    },
    onDropdownChange(parameter: any) {
      parameter.dataDependentParameters?.forEach((element) => {
        this.selectedParameterCheckbox[element.id] = [];
      });
      this.updateParameterDescriptionOnDropdownChange(parameter);
      this.updateDependency(parameter);
    },
    updateParameterDescriptionOnDropdownChange(parameter: any) {
      if (!parameter.parameterValue[0]) return;
      const index = parameter.data?.findIndex((el: { value: string; description: string }) => el.value === parameter.parameterValue[0].value);
      if (index !== -1) parameter.parameterValue[0].description = parameter.data[index].description;
    },
    onInputTextChanged(parameter: any) {
      if (parameter.parameterValue[0] && parameter.initialValue === parameter.parameterValue[0]?.value) return;
      this.updateDependency(parameter);
    },
  },
});
